import { getMedicalRecordAttachmentsObject } from './medicalRecordStatus';

export const MEDICAL_RECORD_VITAL_SIGNS_VERSION = 2 as const;

export interface MedicalRecordBorgMeasures {
  dyspnea?: number;
  fatigue?: number;
}

export interface MedicalRecordVitalSigns {
  version: typeof MEDICAL_RECORD_VITAL_SIGNS_VERSION;
  measuredAt: string;
  heartRateBpm?: number;
  oxygenSaturationPercent?: number;
  respiratoryRateBpm?: number;
  systolicPressureMmHg?: number;
  diastolicPressureMmHg?: number;
  borgInitial?: MedicalRecordBorgMeasures;
  borgFinal?: MedicalRecordBorgMeasures;
}

export interface MedicalRecordVitalSignsDraft {
  measuredAtLocal: string;
  heartRateBpm: string;
  oxygenSaturationPercent: string;
  respiratoryRateBpm: string;
  systolicPressureMmHg: string;
  diastolicPressureMmHg: string;
  borgDyspneaInitial: string;
  borgFatigueInitial: string;
  borgDyspneaFinal: string;
  borgFatigueFinal: string;
}

export interface ParsedMedicalRecordVitalSignsDraft {
  value: MedicalRecordVitalSigns | null;
  error: string | null;
}

export const BORG_CR10_VALUES = [0, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export const BORG_CR10_LABELS: Record<number, string> = {
  0: 'Nenhuma',
  0.5: 'Muito, muito leve',
  1: 'Muito leve',
  2: 'Leve',
  3: 'Moderada',
  4: 'Pouco intensa',
  5: 'Intensa',
  6: '',
  7: 'Muito intensa',
  8: '',
  9: 'Muito, muito intensa',
  10: 'Máxima',
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const parsed = Number(value.trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const isValidBorgValue = (value: number): boolean =>
  BORG_CR10_VALUES.some((option) => option === value);

const optionalInteger = (
  rawValue: string,
  label: string,
  minimum: number,
  maximum: number,
): { value?: number; error?: string } => {
  if (!rawValue.trim()) return {};
  const value = toFiniteNumber(rawValue);
  if (value == null || !Number.isInteger(value)) {
    return { error: `${label} deve ser informada com um número inteiro.` };
  }
  if (value < minimum || value > maximum) {
    return { error: `${label} deve estar entre ${minimum} e ${maximum}.` };
  }
  return { value };
};

const optionalBorg = (rawValue: string, label: string): { value?: number; error?: string } => {
  if (!rawValue.trim()) return {};
  const value = toFiniteNumber(rawValue);
  if (value == null || !isValidBorgValue(value)) {
    return { error: `${label} deve usar um valor válido da escala de Borg CR10.` };
  }
  return { value };
};

const buildBorgMeasures = (
  dyspnea?: number,
  fatigue?: number,
): MedicalRecordBorgMeasures | undefined => {
  if (dyspnea == null && fatigue == null) return undefined;
  return {
    ...(dyspnea != null ? { dyspnea } : {}),
    ...(fatigue != null ? { fatigue } : {}),
  };
};

export const formatBorgValue = (value: number): string => String(value).replace('.', ',');

export const toLocalDateTimeInput = (value?: string | Date | null): string => {
  const parsed = value instanceof Date ? value : value ? new Date(value) : new Date();
  const safeDate = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const localDate = new Date(safeDate.getTime() - safeDate.getTimezoneOffset() * 60_000);
  return localDate.toISOString().slice(0, 16);
};

export const createEmptyMedicalRecordVitalSignsDraft = (
  fallbackMeasuredAt?: string | Date | null,
): MedicalRecordVitalSignsDraft => ({
  measuredAtLocal: toLocalDateTimeInput(fallbackMeasuredAt),
  heartRateBpm: '',
  oxygenSaturationPercent: '',
  respiratoryRateBpm: '',
  systolicPressureMmHg: '',
  diastolicPressureMmHg: '',
  borgDyspneaInitial: '',
  borgFatigueInitial: '',
  borgDyspneaFinal: '',
  borgFatigueFinal: '',
});

export const hasMedicalRecordVitalSignsDraftValues = (
  draft: MedicalRecordVitalSignsDraft,
): boolean =>
  Object.entries(draft).some(
    ([key, value]) => key !== 'measuredAtLocal' && value.trim().length > 0,
  );

export const parseMedicalRecordVitalSignsDraft = (
  draft: MedicalRecordVitalSignsDraft,
  fallbackMeasuredAt?: string | Date | null,
): ParsedMedicalRecordVitalSignsDraft => {
  if (!hasMedicalRecordVitalSignsDraftValues(draft)) {
    return { value: null, error: null };
  }

  const heartRate = optionalInteger(draft.heartRateBpm, 'A frequência cardíaca', 20, 300);
  const oxygenSaturation = optionalInteger(
    draft.oxygenSaturationPercent,
    'A saturação de oxigênio',
    1,
    100,
  );
  const respiratoryRate = optionalInteger(
    draft.respiratoryRateBpm,
    'A frequência respiratória',
    1,
    100,
  );
  const systolicPressure = optionalInteger(
    draft.systolicPressureMmHg,
    'A pressão sistólica',
    40,
    300,
  );
  const diastolicPressure = optionalInteger(
    draft.diastolicPressureMmHg,
    'A pressão diastólica',
    20,
    200,
  );
  const borgDyspneaInitial = optionalBorg(draft.borgDyspneaInitial, 'Borg dispneia inicial');
  const borgFatigueInitial = optionalBorg(draft.borgFatigueInitial, 'Borg fadiga inicial');
  const borgDyspneaFinal = optionalBorg(draft.borgDyspneaFinal, 'Borg dispneia final');
  const borgFatigueFinal = optionalBorg(draft.borgFatigueFinal, 'Borg fadiga final');

  const firstError = [
    heartRate,
    oxygenSaturation,
    respiratoryRate,
    systolicPressure,
    diastolicPressure,
    borgDyspneaInitial,
    borgFatigueInitial,
    borgDyspneaFinal,
    borgFatigueFinal,
  ].find((result) => result.error)?.error;
  if (firstError) return { value: null, error: firstError };

  const hasSystolicPressure = systolicPressure.value != null;
  const hasDiastolicPressure = diastolicPressure.value != null;
  if (hasSystolicPressure !== hasDiastolicPressure) {
    return { value: null, error: 'Para registrar a pressão arterial, informe os valores sistólico e diastólico.' };
  }
  if (
    systolicPressure.value != null &&
    diastolicPressure.value != null &&
    diastolicPressure.value >= systolicPressure.value
  ) {
    return { value: null, error: 'A pressão diastólica deve ser menor que a pressão sistólica.' };
  }

  const measuredAtSource = draft.measuredAtLocal || toLocalDateTimeInput(fallbackMeasuredAt);
  const measuredAtDate = new Date(measuredAtSource);
  if (Number.isNaN(measuredAtDate.getTime())) {
    return { value: null, error: 'Informe uma data e hora válidas para a aferição.' };
  }

  const borgInitial = buildBorgMeasures(borgDyspneaInitial.value, borgFatigueInitial.value);
  const borgFinal = buildBorgMeasures(borgDyspneaFinal.value, borgFatigueFinal.value);

  return {
    value: {
      version: MEDICAL_RECORD_VITAL_SIGNS_VERSION,
      measuredAt: measuredAtDate.toISOString(),
      ...(heartRate.value != null ? { heartRateBpm: heartRate.value } : {}),
      ...(oxygenSaturation.value != null
        ? { oxygenSaturationPercent: oxygenSaturation.value }
        : {}),
      ...(respiratoryRate.value != null ? { respiratoryRateBpm: respiratoryRate.value } : {}),
      ...(systolicPressure.value != null
        ? {
            systolicPressureMmHg: systolicPressure.value,
            diastolicPressureMmHg: diastolicPressure.value,
          }
        : {}),
      ...(borgInitial ? { borgInitial } : {}),
      ...(borgFinal ? { borgFinal } : {}),
    },
    error: null,
  };
};

const readOptionalInteger = (
  value: unknown,
  minimum: number,
  maximum: number,
): number | undefined => {
  const parsed = toFiniteNumber(value);
  return parsed != null && Number.isInteger(parsed) && parsed >= minimum && parsed <= maximum
    ? parsed
    : undefined;
};

const readBorgMeasures = (value: unknown): MedicalRecordBorgMeasures | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const source = value as Record<string, unknown>;
  const dyspnea = toFiniteNumber(source.dyspnea);
  const fatigue = toFiniteNumber(source.fatigue);
  return buildBorgMeasures(
    dyspnea != null && isValidBorgValue(dyspnea) ? dyspnea : undefined,
    fatigue != null && isValidBorgValue(fatigue) ? fatigue : undefined,
  );
};

export const getMedicalRecordVitalSigns = (
  recordAttachments: unknown,
): MedicalRecordVitalSigns | null => {
  const attachments = getMedicalRecordAttachmentsObject(recordAttachments);
  const source = attachments.vitalSigns;
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return null;
  }

  const measuredAt = typeof source.measuredAt === 'string' ? source.measuredAt : '';
  const measuredAtDate = new Date(measuredAt);
  if (Number.isNaN(measuredAtDate.getTime())) return null;

  const heartRateBpm = readOptionalInteger(source.heartRateBpm, 20, 300);
  const oxygenSaturationPercent = readOptionalInteger(source.oxygenSaturationPercent, 1, 100);
  const respiratoryRateBpm = readOptionalInteger(source.respiratoryRateBpm, 1, 100);
  const systolicPressureMmHg = readOptionalInteger(source.systolicPressureMmHg, 40, 300);
  const diastolicPressureMmHg = readOptionalInteger(source.diastolicPressureMmHg, 20, 200);
  const hasValidPressure =
    systolicPressureMmHg != null &&
    diastolicPressureMmHg != null &&
    diastolicPressureMmHg < systolicPressureMmHg;
  const borgInitial = readBorgMeasures(source.borgInitial);
  const borgFinal = readBorgMeasures(source.borgFinal);

  if (
    heartRateBpm == null &&
    oxygenSaturationPercent == null &&
    respiratoryRateBpm == null &&
    !hasValidPressure &&
    !borgInitial &&
    !borgFinal
  ) {
    return null;
  }

  return {
    version: MEDICAL_RECORD_VITAL_SIGNS_VERSION,
    measuredAt: measuredAtDate.toISOString(),
    ...(heartRateBpm != null ? { heartRateBpm } : {}),
    ...(oxygenSaturationPercent != null ? { oxygenSaturationPercent } : {}),
    ...(respiratoryRateBpm != null ? { respiratoryRateBpm } : {}),
    ...(hasValidPressure ? { systolicPressureMmHg, diastolicPressureMmHg } : {}),
    ...(borgInitial ? { borgInitial } : {}),
    ...(borgFinal ? { borgFinal } : {}),
  };
};

export const medicalRecordVitalSignsToDraft = (
  vitalSigns: MedicalRecordVitalSigns | null | undefined,
  fallbackMeasuredAt?: string | Date | null,
): MedicalRecordVitalSignsDraft =>
  vitalSigns
    ? {
        measuredAtLocal: toLocalDateTimeInput(vitalSigns.measuredAt),
        heartRateBpm: vitalSigns.heartRateBpm == null ? '' : String(vitalSigns.heartRateBpm),
        oxygenSaturationPercent:
          vitalSigns.oxygenSaturationPercent == null ? '' : String(vitalSigns.oxygenSaturationPercent),
        respiratoryRateBpm:
          vitalSigns.respiratoryRateBpm == null ? '' : String(vitalSigns.respiratoryRateBpm),
        systolicPressureMmHg:
          vitalSigns.systolicPressureMmHg == null ? '' : String(vitalSigns.systolicPressureMmHg),
        diastolicPressureMmHg:
          vitalSigns.diastolicPressureMmHg == null ? '' : String(vitalSigns.diastolicPressureMmHg),
        borgDyspneaInitial:
          vitalSigns.borgInitial?.dyspnea == null ? '' : String(vitalSigns.borgInitial.dyspnea),
        borgFatigueInitial:
          vitalSigns.borgInitial?.fatigue == null ? '' : String(vitalSigns.borgInitial.fatigue),
        borgDyspneaFinal:
          vitalSigns.borgFinal?.dyspnea == null ? '' : String(vitalSigns.borgFinal.dyspnea),
        borgFatigueFinal:
          vitalSigns.borgFinal?.fatigue == null ? '' : String(vitalSigns.borgFinal.fatigue),
      }
    : createEmptyMedicalRecordVitalSignsDraft(fallbackMeasuredAt);

const formatBorgMeasures = (label: string, measures?: MedicalRecordBorgMeasures): string | null => {
  if (!measures) return null;
  const values = [
    measures.dyspnea == null ? null : `dispneia ${formatBorgValue(measures.dyspnea)}`,
    measures.fatigue == null ? null : `fadiga ${formatBorgValue(measures.fatigue)}`,
  ].filter((value): value is string => Boolean(value));
  return values.length > 0 ? `${label}: ${values.join(', ')}` : null;
};

export const formatMedicalRecordVitalSigns = (
  vitalSigns: MedicalRecordVitalSigns,
  options?: { includeMeasuredAt?: boolean },
): string => {
  const parts = [
    vitalSigns.heartRateBpm == null ? null : `FC ${vitalSigns.heartRateBpm} bpm`,
    vitalSigns.oxygenSaturationPercent == null
      ? null
      : `SpO₂ ${vitalSigns.oxygenSaturationPercent}%`,
    vitalSigns.respiratoryRateBpm == null ? null : `FR ${vitalSigns.respiratoryRateBpm} irpm`,
    vitalSigns.systolicPressureMmHg == null || vitalSigns.diastolicPressureMmHg == null
      ? null
      : `PA ${vitalSigns.systolicPressureMmHg}/${vitalSigns.diastolicPressureMmHg} mmHg`,
    formatBorgMeasures('Borg inicial', vitalSigns.borgInitial),
    formatBorgMeasures('Borg final', vitalSigns.borgFinal),
  ].filter((value): value is string => Boolean(value));

  if (options?.includeMeasuredAt) {
    const measuredAt = new Date(vitalSigns.measuredAt);
    if (!Number.isNaN(measuredAt.getTime())) {
      parts.push(`Aferição: ${measuredAt.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })}`);
    }
  }

  return parts.join(' · ');
};

export const areMedicalRecordVitalSignsEqual = (
  left: MedicalRecordVitalSigns | null,
  right: MedicalRecordVitalSigns | null,
): boolean => JSON.stringify(left) === JSON.stringify(right);
